import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {allRoutes} from '../../../constants/Routes';
import {getPCFRecord} from '../../../services/modules/PCF/pcfAPI';
import usePCFStore from '../../../store/pcfStore/pcfStore';
import showMessage from '../../../utils/showMessage';
import {
  getPCFStepPath,
  syncPcfUrlWithRecordId,
  type PCFStepKey
} from '../utils/pcfRoutes';

interface NavigateToStepOptions {
  replace?: boolean;
}

export const usePCFRecordRoute = (): {
  routeId: string | null;
  recordId: string | null;
  isLoadingRecord: boolean;
  navigateToStep: (step: PCFStepKey, options?: NavigateToStepOptions) => void;
  syncUrlWithRecordId: (newRecordId: string) => void;
} => {
  const {id: routeId} = useParams<{id?: string}>();
  const navigate = useNavigate();
  const location = useLocation();
  const storeRecordId = usePCFStore((state) => state.recordId);
  const setRecordFromApi = usePCFStore((state) => state.setRecordFromApi);
  const [isLoadingRecord, setIsLoadingRecord] = useState(Boolean(routeId));
  const fetchInFlightRef = useRef<string | null>(null);

  useEffect(() => {
    if (!routeId) {
      setIsLoadingRecord(false);

      return;
    }

    if (storeRecordId === routeId) {
      setIsLoadingRecord(false);

      return;
    }

    if (fetchInFlightRef.current === routeId) {
      return;
    }

    fetchInFlightRef.current = routeId;
    setIsLoadingRecord(true);

    getPCFRecord(routeId)
      .then((record) => {
        setRecordFromApi(record);
      })
      .catch(() => {
        showMessage({
          status: 'error',
          message: 'Failed to load study. Please try again.'
        });
        navigate(allRoutes.PCF.studies);
      })
      .finally(() => {
        fetchInFlightRef.current = null;
        setIsLoadingRecord(false);
      });
  }, [routeId, storeRecordId, setRecordFromApi, navigate]);

  const recordId = storeRecordId ?? routeId ?? null;

  const navigateToStep = useCallback(
    (step: PCFStepKey, options?: NavigateToStepOptions) => {
      const currentRecordId =
        usePCFStore.getState().recordId ?? routeId ?? null;

      navigate(getPCFStepPath(step, currentRecordId), {
        replace: options?.replace ?? false
      });
    },
    [navigate, routeId]
  );

  const syncUrlWithRecordId = useCallback(
    (newRecordId: string) => {
      syncPcfUrlWithRecordId(
        navigate,
        location.pathname,
        routeId,
        newRecordId
      );
    },
    [location.pathname, navigate, routeId]
  );

  return {
    routeId: routeId ?? null,
    recordId,
    isLoadingRecord,
    navigateToStep,
    syncUrlWithRecordId
  };
};
